import { useEffect, useState } from "react";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaSpinner,
    FaArrowLeft
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMenuPermissions, getStoredRole, ROLE_SUPERADMIN } from "../utils/auth";

import "./POILocations.css";

const API_URL = process.env.REACT_APP_API_URL;


function CategoryMaster() {

    // Category Master has its own "poi_categories" menu_key in the
    // Role/User Permission matrix - a SuperAdmin can grant/restrict it
    // independently of POI Location.
    const perms = getMenuPermissions("poi_categories");

    const emptyForm = {
        id: "",
        name: "",
        description: "",
        display_order: 0,
        is_active: true
    };

    const [categories, setCategories] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");

    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {

        const value = search.toLowerCase();

        setFiltered(

            categories.filter(item =>
                item.name.toLowerCase().includes(value) ||
                (item.description || "").toLowerCase().includes(value)
            )

        );

    }, [search, categories]);

    const loadCategories = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/poi-categories`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            if (data.status) {

                setCategories(data.data || []);

            } else {

                setCategories([]);

                toast.error(data.message || "Failed to load categories");

            }

        } catch (err) {

            console.error(err);

            setCategories([]);

            toast.error("Unable to load categories. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    const handleChange = (e) => {

        const { name, value, type, checked } = e.target;

        setForm({
            ...form,
            [name]: type === "checkbox" ? checked : value
        });

    };

    const openAdd = () => {

        setIsEdit(false);

        setForm(emptyForm);

        setShowForm(true);

    };

    const openEdit = (item) => {

        setIsEdit(true);

        setForm({
            ...emptyForm,
            ...item
        });

        setShowForm(true);

    };

    const closeForm = () => {

        if (isSaving) return;

        setShowForm(false);

        setForm(emptyForm);

    };

    const saveCategory = async () => {

        if (!form.name) {

            toast.warn("Category name is required");
            return;

        }

        setIsSaving(true);

        const payload = {
            ...form,
            display_order: Number(form.display_order) || 0
        };

        try {

            const res = await fetch(`${API_URL}/portal/poi-categories`, {

                method: isEdit ? "PUT" : "POST",

                headers: {
                    "Content-Type": "application/json"
                },

                body: JSON.stringify(payload)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setShowForm(false);

                setForm(emptyForm);

                toast.success(data.message || "Category saved successfully");

                loadCategories();

            } else {

                toast.error(data.message || "Failed to save category");

            }

        } catch (err) {

            console.error("Failed to save category:", err);

            toast.error("Something went wrong while saving the category");

        } finally {

            setIsSaving(false);

        }

    };

    const deleteCategory = async (id) => {

        const ok = window.confirm(
            "Are you sure you want to delete this category? POI locations already using it are unaffected, but it will disappear from the dropdown."
        );

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/poi-categories/${id}`, {

                method: "DELETE"

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message || "Category deleted successfully");

                loadCategories();

            } else {

                toast.error(data.message || "Failed to delete category");

            }

        } catch (err) {

            console.error("Failed to delete category:", err);

            toast.error("Something went wrong while deleting the category");

        } finally {

            setDeletingId(null);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2>Category Master</h2>
                  {/*   <p className="page-subtitle">
                        Manage the category list used by the POI Location dropdown. Renaming or
                        deleting a category here doesn't change POI locations that already use it -
                        only new selections in the dropdown.
                    </p> */}
                </div>

                {

                    !showForm && perms.add &&

                    <button
                        type="button"
                        className="add-btn"
                        onClick={openAdd}
                    >

                        <FaPlus />

                        Add Category

                    </button>

                }

            </div>

            {

                showForm ? (

                    <div className="inline-form-container">

                        <div className="inline-form-header">

                            <button
                                type="button"
                                className="back-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >

                                <FaArrowLeft />
                                Back to list

                            </button>

                            <h3>
                                {isEdit ? "Update Category" : "Add Category"}
                            </h3>

                        </div>

                        <div className="inline-form-grid">

                            <input
                                name="name"
                                placeholder="Category Name (e.g. Dining, Retail, Navigation)"
                                value={form.name}
                                onChange={handleChange}
                                disabled={isSaving || (isEdit && getStoredRole() !== ROLE_SUPERADMIN)}
                                title={
                                    isEdit && getStoredRole() !== ROLE_SUPERADMIN
                                        ? "Only a SuperAdmin can change the Category Name of an existing entry"
                                        : undefined
                                }
                            />

                            <input
                                name="display_order"
                                type="number"
                                placeholder="Display Order"
                                value={form.display_order}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <textarea
                                name="description"
                                placeholder="Description (optional)"
                                value={form.description || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                                className="full-width"
                                rows={2}
                            />

                            <label className="checkbox-field full-width">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={!!form.is_active}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                />
                                Active (appears in the POI Location dropdown)
                            </label>

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="save-btn"
                                onClick={saveCategory}
                                disabled={isSaving}
                            >

                                {
                                    isSaving
                                        ? <><FaSpinner className="spin" /> Saving...</>
                                        : isEdit
                                            ? "Update Category"
                                            : "Save Category"
                                }

                            </button>

                        </div>

                    </div>

                ) : (

                    <>

                        <div className="search-box">

                            <FaSearch />

                            <input
                                placeholder="Search by name or description..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                        </div>

                        <div className="table-container">

                            <table>

                                <thead>

                                    <tr>
                                        <th>Name</th>
                                        <th>Description</th>
                                        <th>Order</th>
                                        <th>Status</th>
                                        <th style={{ width: "120px" }}>Action</th>
                                    </tr>

                                </thead>

                                <tbody>

                                    {

                                        isLoading ?

                                            <tr>
                                                <td colSpan="5" style={{ textAlign: "center", padding: "40px" }}>
                                                    <FaSpinner className="spin" /> Loading categories...
                                                </td>
                                            </tr>

                                            :

                                            filtered.length === 0 ?

                                                <tr>
                                                    <td colSpan="5" style={{ textAlign: "center", padding: "40px" }}>
                                                        No categories yet - click "Add Category" to get started.
                                                    </td>
                                                </tr>

                                                :

                                                filtered.map(item => (

                                                    <tr key={item.id}>

                                                        <td>{item.name}</td>

                                                        <td>{item.description || "-"}</td>

                                                        <td>{item.display_order}</td>

                                                        <td>
                                                            <span className={`status-pill ${item.is_active ? "active" : "inactive"}`}>
                                                                {item.is_active ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>

                                                        <td>

                                                            <div className="action-buttons">

                                                                {perms.edit &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn edit-btn"
                                                                    title="Edit category"
                                                                    aria-label="Edit category"
                                                                    onClick={() => openEdit(item)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    <FaEdit />
                                                                </button>
                                                                }

                                                                {perms.delete &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn delete-btn"
                                                                    title="Delete category"
                                                                    aria-label="Delete category"
                                                                    onClick={() => deleteCategory(item.id)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    {
                                                                        deletingId === item.id
                                                                            ? <FaSpinner className="spin" />
                                                                            : <FaTrash />
                                                                    }
                                                                </button>
                                                                }

                                                            </div>

                                                        </td>

                                                    </tr>

                                                ))

                                    }

                                </tbody>

                            </table>

                        </div>

                    </>

                )

            }

        </div>

    );

}

export default CategoryMaster;
